import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { landingRoute } from '~/utils/constants/routes';
import { AnimatedSection, SEO } from '~/components/common';
import { signInURL, signUpURL } from '~/utils/constants/env';

const Home: React.FC = () => {
  //#region Declare Hook
  const { t } = useTranslation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  const features = [
    {
      icon: '💬',
      titleKey: 'home.features.aiChat.title',
      descKey: 'home.features.aiChat.description',
      gradient: 'from-lumi-500 to-lumi-cyan-500',
    },
    {
      icon: '🎯',
      titleKey: 'home.features.goals.title',
      descKey: 'home.features.goals.description',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: '📓',
      titleKey: 'home.features.journal.title',
      descKey: 'home.features.journal.description',
      gradient: 'from-amber-500 to-orange-500',
    },
    {
      icon: '🔁',
      titleKey: 'home.features.habits.title',
      descKey: 'home.features.habits.description',
      gradient: 'from-emerald-500 to-teal-500',
    },
  ];

  const steps = [
    { number: '01', titleKey: 'home.howItWorks.step1.title', descKey: 'home.howItWorks.step1.description' },
    { number: '02', titleKey: 'home.howItWorks.step2.title', descKey: 'home.howItWorks.step2.description' },
    { number: '03', titleKey: 'home.howItWorks.step3.title', descKey: 'home.howItWorks.step3.description' },
  ];

  const whyLumi = [
    { icon: '🧠', titleKey: 'home.whyLumi.item1.title', descKey: 'home.whyLumi.item1.description' },
    { icon: '🔒', titleKey: 'home.whyLumi.item2.title', descKey: 'home.whyLumi.item2.description' },
    { icon: '✨', titleKey: 'home.whyLumi.item3.title', descKey: 'home.whyLumi.item3.description' },
    { icon: '📱', titleKey: 'home.whyLumi.item4.title', descKey: 'home.whyLumi.item4.description' },
    { icon: '⚡', titleKey: 'home.whyLumi.item5.title', descKey: 'home.whyLumi.item5.description' },
    { icon: '🎁', titleKey: 'home.whyLumi.item6.title', descKey: 'home.whyLumi.item6.description' },
  ];

  return (
    <div className='min-h-screen bg-lumi-dark-300 text-white'>
      <SEO type='home' />
      {/* Hero Section */}
      <section className='relative min-h-screen flex items-center justify-center overflow-hidden pt-16'>
        {/* Background gradient */}
        <div className='absolute inset-0 bg-gradient-to-b from-lumi-dark-300 via-lumi-dark-200 to-lumi-dark-300' />
        <div className='absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-lumi-700/20 blur-[120px] pointer-events-none' />
        <div className='absolute top-1/2 left-1/4 w-[300px] h-[300px] rounded-full bg-lumi-cyan-500/10 blur-[80px] pointer-events-none' />

        <div className='relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          {/* Badge */}
          <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lumi-600/20 border border-lumi-500/30 text-lumi-400 text-sm font-medium mb-8 animate-fade-in'>
            <span className='w-2 h-2 rounded-full bg-lumi-400 animate-pulse' />
            {t('home.hero.badge')}
          </div>

          {/* Title */}
          <h1 className='text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-6 animate-slide-up'>
            <span className='bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent'>
              {t('home.hero.titleStart')}{' '}
            </span>
            <span className='bg-gradient-to-r from-lumi-400 to-lumi-cyan-400 bg-clip-text text-transparent'>
              {t('home.hero.titleHighlight')}
            </span>
            <span className='bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent'>
              {' '}
              {t('home.hero.titleEnd')}
            </span>
          </h1>

          {/* Subtitle */}
          <p className='text-lg sm:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed animate-slide-up'>
            {t('home.hero.subtitle')}
          </p>

          {/* CTAs */}
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4 mb-16'>
            <a
              href={signUpURL}
              className='w-full sm:w-auto px-8 py-4 rounded-xl bg-lumi-600 hover:bg-lumi-500 text-white font-semibold text-base transition-all duration-200 hover:shadow-xl hover:shadow-lumi-600/40 hover:-translate-y-0.5'
            >
              {t('home.hero.cta')}
            </a>
            <Link
              to={landingRoute.features}
              className='w-full sm:w-auto px-8 py-4 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-base transition-all duration-200 hover:-translate-y-0.5'
            >
              {t('home.hero.ctaSecondary')}
            </Link>
          </div>

          {/* Social Proof */}
          <div className='flex items-center justify-center gap-2 text-white/40 text-sm'>
            <span className='text-base'>🚀</span>
            <span>{t('home.hero.socialProof')}</span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className='absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30'>
          <div className='w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-1.5'>
            <div className='w-1 h-2 rounded-full bg-white/40 animate-bounce' />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className='py-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-6xl mx-auto'>
          <AnimatedSection animation='slide-up' className='text-center mb-16'>
            <h2 className='text-3xl sm:text-4xl font-bold text-white mb-4'>{t('home.features.title')}</h2>
            <p className='text-white/50 text-lg max-w-2xl mx-auto'>{t('home.features.subtitle')}</p>
          </AnimatedSection>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
            {features.map((feature, index) => (
              <AnimatedSection
                key={feature.titleKey}
                animation='slide-up'
                delay={index * 100}
                className='group p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-lumi-500/40 hover:bg-white/8 transition-all duration-300 hover:-translate-y-1'
              >
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-2xl mb-5`}
                >
                  {feature.icon}
                </div>
                <h3 className='text-white font-semibold text-lg mb-3'>{t(feature.titleKey)}</h3>
                <p className='text-white/50 text-sm leading-relaxed'>{t(feature.descKey)}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className='py-24 px-4 sm:px-6 lg:px-8 bg-white/[0.02]'>
        <div className='max-w-6xl mx-auto'>
          <AnimatedSection animation='slide-up' className='text-center mb-16'>
            <h2 className='text-3xl sm:text-4xl font-bold text-white mb-4'>{t('home.howItWorks.title')}</h2>
          </AnimatedSection>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            {steps.map((step, index) => (
              <AnimatedSection
                key={step.number}
                animation='slide-up'
                delay={index * 150}
                className='relative flex flex-col items-center text-center'
              >
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className='hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-gradient-to-r from-lumi-500/40 to-transparent' />
                )}
                <div className='w-16 h-16 rounded-2xl bg-gradient-to-br from-lumi-600 to-lumi-800 border border-lumi-500/30 flex items-center justify-center text-lumi-300 font-bold text-xl mb-6 relative z-10'>
                  {step.number}
                </div>
                <h3 className='text-white font-semibold text-lg mb-3'>{t(step.titleKey)}</h3>
                <p className='text-white/50 text-sm leading-relaxed max-w-xs'>{t(step.descKey)}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Lumi Section */}
      <section className='py-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-6xl mx-auto'>
          <AnimatedSection animation='slide-up' className='text-center mb-16'>
            <h2 className='text-3xl sm:text-4xl font-bold text-white mb-4'>{t('home.whyLumi.title')}</h2>
            <p className='text-white/50 text-lg max-w-2xl mx-auto'>{t('home.whyLumi.subtitle')}</p>
          </AnimatedSection>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
            {whyLumi.map((item, index) => (
              <AnimatedSection
                key={item.titleKey}
                animation='slide-up'
                delay={index * 100}
                className='flex gap-4 p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-lumi-500/30 hover:bg-white/8 transition-all duration-300'
              >
                <div className='text-3xl flex-shrink-0'>{item.icon}</div>
                <div>
                  <h3 className='text-white font-semibold text-base mb-2'>{t(item.titleKey)}</h3>
                  <p className='text-white/50 text-sm leading-relaxed'>{t(item.descKey)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='py-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-3xl mx-auto text-center'>
          <AnimatedSection
            animation='scale-in'
            className='p-12 rounded-3xl bg-gradient-to-br from-lumi-800/50 to-lumi-dark-200 border border-lumi-500/30 relative overflow-hidden'
          >
            <div className='absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-lumi-600/20 blur-[60px] pointer-events-none' />
            <div className='relative z-10'>
              <h2 className='text-3xl sm:text-4xl font-bold text-white mb-4'>{t('home.cta.title')}</h2>
              <p className='text-white/50 text-lg mb-8'>{t('home.cta.subtitle')}</p>
              <a
                href={signInURL}
                className='inline-flex items-center px-8 py-4 rounded-xl bg-lumi-600 hover:bg-lumi-500 text-white font-semibold text-base transition-all duration-200 hover:shadow-xl hover:shadow-lumi-600/40 hover:-translate-y-0.5'
              >
                {t('home.cta.button')}
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
